import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { 
  Activity,
  Eye,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  X,
  Clock,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertCircle,
  CheckCircle,
  UserCheck
} from 'lucide-react';
import { Job, JobsQueryParams, jobService } from '../../services/job';

interface JobsListProps {
  batchId?: string;
  storeId?: string;
  statusFilter?: string;
  batchIdFilter?: string;
  onJobSelect?: (job: Job) => void;
  onApproveJob?: (jobId: string) => void;
  onRejectJob?: (jobId: string, reason?: string) => void;
  onBulkReject?: (jobIds: string[], reason?: string) => void;
  onJobStatusUpdate?: (jobId: string, oldStatus: string, newStatus: string) => void;
}

export function JobsList({ batchId, storeId, statusFilter, batchIdFilter, onJobSelect, onApproveJob, onRejectJob, onBulkReject, onJobStatusUpdate }: JobsListProps) {
  const [jobs, setJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<JobsQueryParams>({
    batch_id: batchId || batchIdFilter,
    store_id: storeId,
    status: statusFilter,
    limit: 20,
    offset: 0,
    sort_by: 'created_at',
    sort_order: 'desc'
  });
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 20,
    offset: 0,
    has_more: false
  });
  const [rejectingJob, setRejectingJob] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processingJobs, setProcessingJobs] = useState<Set<string>>(new Set());
  const [lastStatusUpdates, setLastStatusUpdates] = useState<Record<string, string>>({});

  useEffect(() => {
    loadJobs();
  }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Public method to update job status from external polling
  const updateJobStatus = useCallback((jobId: string, newStatus: string) => {
    setLastStatusUpdates(prev => ({ ...prev, [jobId]: newStatus }));
    
    // Update jobs array if the job is currently displayed
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === jobId 
          ? { ...job, status: newStatus as Job['status'] }
          : job
      )
    );
  }, []);
  
  // Expose method to parent via callback
  useEffect(() => {
    if (onJobStatusUpdate) {
      // This allows parent to call updateJobStatus
      (window as any).__updateJobStatus = updateJobStatus;
    }
    
    return () => {
      delete (window as any).__updateJobStatus;
    };
  }, [updateJobStatus, onJobStatusUpdate]);

  // Update filters when props change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      batch_id: batchId || batchIdFilter,
      store_id: storeId,
      status: statusFilter,
      offset: 0 // Reset to first page when filters change
    }));
  }, [batchId, storeId, statusFilter, batchIdFilter]);

  const loadJobs = async () => {
    setIsLoading(true);
    try {
      const response = await jobService.getJobs(filters);
      const fetchedJobs = response.data.jobs;
      
      // Apply any pending status updates to fetched jobs
      const updatedJobs = fetchedJobs.map(job => {
        if (lastStatusUpdates[job.id]) {
          return { ...job, status: lastStatusUpdates[job.id] as Job['status'] };
        }
        return job;
      });
      
      setJobs(updatedJobs);
      setPagination(response.data.pagination);
      setError(null);
    } catch (error: any) {
      setError(error.message || 'Failed to load jobs');
    } finally {
      setIsLoading(false);
    }
  };

  const updateFilter = (key: keyof JobsQueryParams, value: any) => {
    setFilters({
      ...filters,
      [key]: value,
      offset: 0 // Reset to first page when filtering
    });
  };

  const nextPage = () => {
    if (pagination.has_more) {
      setFilters({
        ...filters,
        offset: pagination.offset + pagination.limit
      });
    }
  };

  const prevPage = () => {
    if (pagination.offset > 0) {
      setFilters({
        ...filters,
        offset: Math.max(0, pagination.offset - pagination.limit)
      });
    }
  };

  const cancelJob = async (jobId: string) => {
    try {
      // Optimistic update
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId 
            ? { ...job, status: 'cancelled' as const }
            : job
        )
      );
      
      // Track status update
      setLastStatusUpdates(prev => ({ ...prev, [jobId]: 'cancelled' }));
      
      // Notify parent of status change
      const previousStatus = jobs.find(j => j.id === jobId)?.status || 'pending';
      onJobStatusUpdate?.(jobId, previousStatus, 'cancelled');
      
      await jobService.cancelJob(jobId);
    } catch (error: any) {
      setError(error.message || 'Failed to cancel job');
      // Revert on error
      loadJobs();
    }
  };

  const handleApproveJob = async (jobId: string) => {
    setProcessingJobs(prev => new Set(prev).add(jobId));
    
    // Optimistic update - update the job status locally for instant feedback
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === jobId 
          ? { ...job, status: 'approved' as const }
          : job
      )
    );
    
    // Track status update
    setLastStatusUpdates(prev => ({ ...prev, [jobId]: 'approved' }));
    
    // Notify parent of status change
    const previousStatus = jobs.find(j => j.id === jobId)?.status || 'processed_pending_approval';
    onJobStatusUpdate?.(jobId, previousStatus, 'approved');
    
    try {
      await onApproveJob?.(jobId);
      // Don't refresh the entire list, just keep the optimistic update
    } catch (error: any) {
      setError(error.message || 'Failed to approve job');
      // Revert the optimistic update on error
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === jobId 
            ? { ...job, status: 'processed_pending_approval' as const }
            : job
        )
      );
      setLastStatusUpdates(prev => ({ ...prev, [jobId]: 'processed_pending_approval' }));
    } finally {
      setProcessingJobs(prev => {
        const newSet = new Set(prev);
        newSet.delete(jobId);
        return newSet;
      });
    }
  };

  const handleRejectWithReason = async () => {
    if (!rejectingJob) return;
    
    // Optimistic update - update the job status locally for instant feedback
    setJobs(prevJobs => 
      prevJobs.map(job => 
        job.id === rejectingJob 
          ? { ...job, status: 'rejected' as const }
          : job
      )
    );
    
    // Track status update
    setLastStatusUpdates(prev => ({ ...prev, [rejectingJob]: 'rejected' }));
    
    // Notify parent of status change
    const previousStatus = jobs.find(j => j.id === rejectingJob)?.status || 'processed_pending_approval';
    onJobStatusUpdate?.(rejectingJob, previousStatus, 'rejected');
    
    try {
      await onRejectJob?.(rejectingJob, rejectReason);
      setRejectingJob(null);
      setRejectReason('');
      // Don't refresh the entire list, just keep the optimistic update
    } catch (error: any) {
      setError(error.message || 'Failed to reject job');
      // Revert the optimistic update on error
      setJobs(prevJobs => 
        prevJobs.map(job => 
          job.id === rejectingJob 
            ? { ...job, status: 'processed_pending_approval' as const }
            : job
        )
      );
      setLastStatusUpdates(prev => ({ ...prev, [rejectingJob]: 'processed_pending_approval' }));
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4" />;
      case 'processing': return <Loader2 className="w-4 h-4 animate-spin" />;
      case 'processed_pending_approval': return <AlertCircle className="w-4 h-4" />;
      case 'approved': return <CheckCircle2 className="w-4 h-4" />;
      case 'complete': return <CheckCircle2 className="w-4 h-4" />;
      case 'failed': return <XCircle className="w-4 h-4" />;
      case 'cancelled': return <X className="w-4 h-4" />;
      case 'rejected': return <XCircle className="w-4 h-4" />;
      case 'uploading_to_magento': return <Loader2 className="w-4 h-4 animate-spin" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      {batchId && (
        <div className="mb-4">
          <p className="text-muted-foreground">
            Batch: {batchId.slice(0, 8)}...
          </p>
        </div>
      )}

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Compact Display Controls */}
      <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4 border">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Show:</label>
            <Select
              value={(filters.limit || 20).toString()}
              onValueChange={(value) => updateFilter('limit', parseInt(value))}
            >
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="20">20</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground">items per page</span>
          </div>

          <div className="flex items-center gap-2">
            <label className="text-sm font-medium">Sort:</label>
            <Select
              value={filters.sort_order || 'desc'}
              onValueChange={(value) => {
                setFilters({
                  ...filters,
                  sort_order: value as 'asc' | 'desc',
                  offset: 0 // Reset to first page when sorting changes
                });
              }}
            >
              <SelectTrigger className="w-32 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="desc">Newest First</SelectItem>
                <SelectItem value="asc">Oldest First</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {statusFilter && (
            <div className="flex items-center gap-2 px-3 py-1 bg-primary/10 rounded-full">
              <Filter className="w-3 h-3 text-primary" />
              <span className="text-xs font-medium text-primary">
                {statusFilter.replace(/_/g, ' ')}
              </span>
            </div>
          )}
        </div>

        <div className="text-sm text-muted-foreground">
          {pagination.total > 0 ? `${pagination.total} total jobs` : 'No jobs found'}
        </div>
      </div>

      {/* Bulk Actions for Pending Approval Jobs */}
      {onBulkReject && jobs.some(job => job.status === 'processed_pending_approval') && (
        <Card className="border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-950/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-medium text-orange-800 dark:text-orange-200">Bulk Actions</h3>
                <p className="text-sm text-orange-600 dark:text-orange-300">
                  {jobs.filter(job => job.status === 'processed_pending_approval').length} jobs pending approval
                </p>
              </div>
              <Button
                onClick={() => {
                  const pendingJobs = jobs.filter(job => job.status === 'processed_pending_approval');
                  if (pendingJobs.length > 0) {
                    onBulkReject(pendingJobs.map(job => job.id), 'Bulk rejection from jobs list');
                  }
                }}
                variant="destructive"
                className="bg-red-600 hover:bg-red-700"
              >
                <XCircle className="h-4 w-4 mr-2" />
                Reject All Pending ({jobs.filter(job => job.status === 'processed_pending_approval').length})
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Jobs List */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading jobs...</span>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-muted-foreground">No jobs found matching your criteria.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {jobs.map((job) => {
                // Check if we have a newer status update for this job
                const currentStatus = lastStatusUpdates[job.id] || job.status;
                // Ensure we have a valid status
                const validStatus = currentStatus || 'pending';
                const displayJob = lastStatusUpdates[job.id] ? { ...job, status: validStatus as Job['status'] } : job;
                const statusDisplay = jobService.getStatusDisplay(displayJob.status);
                
                return (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => onJobSelect?.(job)}
                  >
                    <div className="flex items-center gap-4">
                      <img
                        src={job.original_image_url || job.processed_image_url}
                        alt="Product"
                        className="w-16 h-16 rounded object-cover"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/placeholder-image.png';
                        }}
                      />
                      
                      <div className="space-y-1">
                        <div className="font-medium">{job.products.name}</div>
                        <div className="text-sm text-muted-foreground">
                          Store: {job.stores.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {job.transformations.length} transformation{job.transformations.length !== 1 ? 's' : ''}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Created: {formatDate(job.created_at)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <Badge 
                          variant="secondary" 
                          className={`${statusDisplay.color} border-current`}
                        >
                          <span className="mr-1">{getStatusIcon(displayJob.status)}</span>
                          {statusDisplay.label}
                        </Badge>
                        {job.error_message && (
                          <div className="text-xs text-destructive mt-1 max-w-xs truncate">
                            {job.error_message}
                          </div>
                        )}
                      </div>

                      {['pending', 'processing'].includes(displayJob.status) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            cancelJob(job.id);
                          }}
                        >
                          Cancel
                        </Button>
                      )}

                      {displayJob.status === 'processed_pending_approval' && onApproveJob && (
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setRejectingJob(job.id);
                            }}
                            className="text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/30"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Reject
                          </Button>
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApproveJob(job.id);
                            }}
                            disabled={processingJobs.has(job.id)}
                            className="bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-800"
                          >
                            {processingJobs.has(job.id) ? (
                              <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            ) : (
                              <CheckCircle className="w-4 h-4 mr-1" />
                            )}
                            {processingJobs.has(job.id) ? 'Approving...' : 'Approve'}
                          </Button>
                        </div>
                      )}

                      {onJobSelect && (
                        <Button variant="ghost" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {jobs.length > 0 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {pagination.offset + 1} to {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total} jobs
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={prevPage}
                  disabled={pagination.offset === 0}
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextPage}
                  disabled={!pagination.has_more}
                >
                  Next
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reject Modal */}
      {rejectingJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>Reject Image</CardTitle>
              <p className="text-sm text-muted-foreground">
                Please provide a reason for rejecting this processed image.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder="Optional: Reason for rejection"
                value={rejectReason}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRejectReason(e.target.value)}
                rows={3}
              />
              <div className="flex items-center gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setRejectingJob(null);
                    setRejectReason('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRejectWithReason}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Reject
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}